import type { BlockContentProps } from "@sanity/block-content-to-react";
import BlockContent from "@sanity/block-content-to-react";
import styled from "@emotion/styled";
import type { FunctionComponent, ReactElement } from "react";
import React, { useMemo } from "react";
import { applyCmsImageParams } from "@9amhealth/cms-api";
import type {
  Box,
  CmsImageRootObject,
  SimpleCmsImageData
} from "src/types/sanitySchema";
import { useSanityAssets } from "./useSanityAssets";

const Async = {
  CareTeamSlider: React.lazy(
    async () => import("src/ui/components/CareTeamSlider/CareTeamSlider")
  ),
  InfoBox: React.lazy(async () => import("src/ui/components/InfoBox/InfoBox")),
  ImageList: React.lazy(
    async () =>
      import("src/ui/components/SanityBlockContent/components/ImageList")
  )
};

const SANITY_IMAGE_WIDTH_PX = 300;

const InlineImage = styled.img`
  border-radius: unset;
`;

interface SerializerNode<T> {
  node: T;
}

interface CareTeamSliderObject {
  members: { _ref: string }[];
}

interface InfoBoxObject {
  boxDecorations: boolean;
  boxes: Box[];
}

interface ImageListObject {
  images: SimpleCmsImageData[];
}

interface SanityImageProps {
  assetRef: string;
  className?: string;
}

const SanityImage = ({
  assetRef,
  className
}: SanityImageProps): ReactElement => {
  const assets = useSanityAssets(useMemo(() => [assetRef], [assetRef]));
  const image: CmsImageRootObject | undefined = assets[0];

  if (!image) {
    return <span></span>;
  }

  const src = applyCmsImageParams(image.url, { width: SANITY_IMAGE_WIDTH_PX });

  return (
    <InlineImage className={className} src={src} alt={image.originalFilename} />
  );
};

interface SerializerOptions {
  imageClassName?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Serializers = Record<string, any>;

function buildSerializers({ imageClassName }: SerializerOptions): Serializers {
  return {
    types: {
      careTeamSlider: ({
        node
      }: SerializerNode<CareTeamSliderObject>): ReactElement => {
        const refs = node.members.map((member) => member._ref);
        return <Async.CareTeamSlider refs={refs} />;
      },
      infoBoxes: ({ node }: SerializerNode<InfoBoxObject>): ReactElement => (
        <Async.InfoBox boxes={node.boxes} />
      ),
      reviews: (): ReactElement => <span></span>,
      imageList: ({ node }: SerializerNode<ImageListObject>): ReactElement => (
        <Async.ImageList images={node.images} />
      ),
      image: ({ node }: SerializerNode<SimpleCmsImageData>): ReactElement => (
        <SanityImage assetRef={node.asset._ref} className={imageClassName} />
      )
    }
  };
}

export type SanityContentBlock = {
  _type: string;
};

interface SanityBlockContentProps extends BlockContentProps {
  imageClassName?: string;
  blocks: SanityContentBlock | SanityContentBlock[] | undefined;
}

const SanityBlockContent: FunctionComponent<SanityBlockContentProps> = ({
  imageClassName,
  ...props
}): React.ReactElement => {
  const serializers = useMemo(
    () => buildSerializers({ imageClassName }),
    [imageClassName]
  );

  return (
    <BlockContent
      {...props}
      blocks={props.blocks ?? []}
      serializers={serializers}
    />
  );
};

export default SanityBlockContent;
